import Button from '@mui/material/Button';
import { Link, Tooltip } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useBoolean } from 'src/hooks/use-boolean';

import { fDateTime } from 'src/utils/format-time';
import { hasPermission } from 'src/utils/has-permissions';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';

import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { IPackagingDamageDispatchListItem } from 'src/types/packaging-damage-dispatch';

type Props = {
  row: IPackagingDamageDispatchListItem;
  onDeleteRow: VoidFunction;
  // Backend restricts delete to the most recently created damage dispatch
  // — this row is only actionable when it's confirmed (by the list view)
  // to be that record.
  isEditable: boolean;
};

export default function PackagingDamageDispatchTableRow({
  row,
  onDeleteRow,
  isEditable,
}: Props) {
  const { _id, damageCode, damageDate, remarks, sourceReturn } = row;

  const { user: authUser } = useAuthContext();

  const confirm = useBoolean();

  const popover = usePopover();

  // Auto-created-from-Return records are locked — the backend refuses to
  // delete them directly regardless of last-transaction status, so the
  // delete action is hidden entirely rather than shown and then failing.
  const canManage =
    isEditable &&
    !sourceReturn &&
    hasPermission(
      authUser,
      'any',
      AuthPermissions.DELETE_PACKAGING_DAMAGE_DISPATCH,
      AuthPermissions.ADMIN_PERMISSION,
      AuthPermissions.SUPER_ADMIN_PERMISSION
    );

  return (
    <>
      <Link
        component={RouterLink}
        href={paths.dashboard.packagingDamageDispatch.view(_id)}
        sx={{
          textDecoration: 'none',
          color: 'inherit',
          display: 'contents',
        }}
      >
        <TableRow hover>
          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
            {damageCode}
          </TableCell>

          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
            {fDateTime(damageDate)}
          </TableCell>

          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
            {remarks ?? '-'}
          </TableCell>

          <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
            <Link
              component={RouterLink}
              href={paths.dashboard.packagingDamageDispatch.view(_id)}
              mr={2}
            >
              <Button variant="soft" color="primary">
                View More
              </Button>
            </Link>

            {canManage && (
              <Tooltip title="More options">
                <IconButton
                  color={popover.open ? 'inherit' : 'default'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    popover.onOpen(e);
                  }}
                >
                  <Iconify icon="eva:more-vertical-fill" />
                </IconButton>
              </Tooltip>
            )}

            {sourceReturn ? (
              <Tooltip title="Auto-created from a return — delete the return to reverse this">
                <span>
                  <IconButton disabled>
                    <Iconify icon="eva:lock-outline" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              !isEditable && (
                <Tooltip title="Only the most recently created damage dispatch can be deleted">
                  <span>
                    <IconButton disabled>
                      <Iconify icon="eva:lock-outline" />
                    </IconButton>
                  </span>
                </Tooltip>
              )
            )}
          </TableCell>
        </TableRow>
      </Link>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 140 }}
      >
        <MenuItem
          onClick={() => {
            confirm.onTrue();
            popover.onClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon="solar:trash-bin-trash-bold" />
          Delete
        </MenuItem>
      </CustomPopover>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Delete Damage Dispatch"
        content={`Are you sure you want to delete ${damageCode}? This will reverse its stock effect.`}
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              confirm.onFalse();
              onDeleteRow();
            }}
          >
            Delete
          </Button>
        }
      />
    </>
  );
}
